import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d67jgtfmvg9ih2c796ng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-orange-50">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="px-16 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/1477363d-ff54-47e4-8b64-86359d408f82.png" alt="Poljana d. o. o." className="h-10 w-10 object-contain" />
            <span className="text-lg font-light tracking-wide text-gray-800">Poljana d. o. o.</span>
          </div>
          <div className="bg-white/85 backdrop-blur-sm px-6 py-3 rounded-full shadow-sm flex items-center gap-2">
            <i className="ri-shield-check-line text-teal-600"></i>
            <span className="text-sm text-gray-700">iOS Experts</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-48 pb-32 px-16">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-7xl font-semibold leading-tight text-gray-900 max-w-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            Modern iOS Applications for Enhanced Security
          </h1>
        </div>
      </section>

      {/* Products Section */}
      <section className="pb-32 px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-8">
            {/* VPN Card */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-80 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-8">
                <div className="relative">
                  <div className="w-48 h-96 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl shadow-2xl flex items-center justify-center">
                    <i className="ri-shield-check-fill text-white text-6xl"></i>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-3xl font-semibold mb-3 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>VPN</h3>
                <p className="text-gray-600 leading-relaxed">Secure your connection and protect your privacy across all networks</p>
              </div>
            </div>

            {/* Cleaner Card */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-80 bg-gradient-to-br from-orange-50 to-teal-50 flex items-center justify-center p-8">
                <div className="relative">
                  <div className="w-48 h-96 bg-gradient-to-br from-orange-400 to-teal-400 rounded-3xl shadow-2xl flex items-center justify-center">
                    <div className="relative">
                      <div className="w-32 h-32 rounded-full border-8 border-white/30 flex items-center justify-center">
                        <i className="ri-delete-bin-line text-white text-5xl"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-3xl font-semibold mb-3 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Cleaner</h3>
                <p className="text-gray-600 leading-relaxed">Optimize device performance and free up valuable storage space efficiently</p>
              </div>
            </div>

            {/* Expertise Card */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden">
              <div className="h-80 bg-gradient-to-br from-purple-100 via-blue-100 to-pink-100 flex items-center justify-center p-8">
                <div className="relative w-full h-full flex items-center justify-center">
                  <div className="absolute w-32 h-32 bg-gradient-to-br from-purple-400 to-blue-500 rounded-3xl opacity-80 transform rotate-12"></div>
                  <div className="absolute w-32 h-32 bg-gradient-to-br from-blue-400 to-pink-500 rounded-3xl opacity-80 transform -rotate-12"></div>
                  <div className="absolute w-24 h-24 bg-gradient-to-br from-pink-400 to-purple-500 rounded-3xl opacity-90"></div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-3xl font-semibold mb-3 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>iOS Platform Expertise</h3>
                <p className="text-gray-600 leading-relaxed">Delivering cutting-edge solutions with deep technical knowledge and innovation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="pb-32 px-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-xs uppercase tracking-widest text-gray-400 mb-4">About Our Team</div>
          <p className="text-4xl leading-relaxed text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Poljana d. o. o. is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. 🛡️
          </p>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="pb-32 px-16">
        <div className="max-w-7xl mx-auto grid grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-sm text-gray-500 mb-8 flex items-center gap-2">
              <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
              User Feedback
            </div>
            <div className="relative">
              <span className="text-8xl text-gray-200 absolute -top-8 -left-4" style={{ fontFamily: 'Playfair Display, serif' }}>"</span>
              <p className="text-5xl leading-snug text-gray-900 relative z-10" style={{ fontFamily: 'Playfair Display, serif' }}>
                The attention to detail in their applications demonstrates true iOS development mastery
              </p>
              <div className="mt-8 flex items-center gap-2">
                <span className="text-gray-600">— iOS Developer Community</span>
                <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                  <i className="ri-star-fill text-white text-xs"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="h-96 bg-gradient-to-br from-purple-100 via-blue-100 to-pink-100 rounded-3xl flex items-center justify-center">
            <div className="relative w-64 h-64">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-300 to-blue-400 rounded-full opacity-60 blur-2xl"></div>
              <div className="absolute inset-8 bg-gradient-to-br from-blue-300 to-pink-400 rounded-full opacity-60 blur-xl"></div>
              <div className="absolute inset-16 bg-gradient-to-br from-pink-300 to-purple-400 rounded-full opacity-60 blur-lg"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="bg-orange-50/30 py-20 px-16">
        <div className="max-w-7xl mx-auto grid grid-cols-3 gap-10">
          {/* Security */}
          <div className="bg-gray-900 rounded-3xl p-8 text-white">
            <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mb-6">
              <i className="ri-lock-fill text-white text-xl"></i>
            </div>
            <h3 className="text-3xl font-semibold mb-3">Enterprise-Grade Security</h3>
            <p className="text-gray-300 text-sm">Military-level encryption protocols</p>
          </div>

          {/* Performance */}
          <div className="relative bg-gradient-to-br from-teal-500 to-blue-600 rounded-3xl p-8 text-white overflow-hidden">
            <div className="absolute inset-0 bg-black/20"></div>
            <div className="relative z-10">
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full inline-flex items-center gap-2 mb-6">
                <i className="ri-speed-fill text-white"></i>
                <span className="text-sm">Performance</span>
              </div>
              <h3 className="text-3xl font-bold">Optimized Efficiency</h3>
            </div>
          </div>

          {/* Innovation */}
          <div className="bg-white rounded-3xl p-8">
            <h3 className="text-3xl font-semibold mb-3 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>Continuous Innovation</h3>
            <p className="text-gray-600 text-sm mb-6">Regular updates and improvements</p>
            <div className="flex gap-2">
              <div className="w-16 h-16 bg-orange-400 rounded-2xl"></div>
              <div className="w-16 h-16 bg-teal-400 rounded-2xl"></div>
              <div className="w-16 h-16 bg-purple-400 rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-emerald-900 text-white py-20 px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-4 gap-12 mb-16">
            {/* Company */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/1477363d-ff54-47e4-8b64-86359d408f82.png" alt="Poljana d. o. o." className="h-8 w-8 object-contain" />
                <span className="text-lg font-light text-gray-300">Poljana d. o. o.</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Expert iOS development team creating secure and efficient mobile applications
              </p>
            </div>

            {/* Location */}
            <div>
              <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-4">Location</h4>
              <div className="space-y-3 text-sm text-gray-200">
                <div className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-teal-400 mt-1"></i>
                  <div>
                    <div>Address: Osjecka 34</div>
                    <div>City: Pozega</div>
                    <div>Zip: 34000</div>
                    <div>Country: Croatia</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-4">Contact</h4>
              <div className="space-y-3 text-sm text-gray-200">
                <div className="flex items-center gap-2">
                  <i className="ri-mail-line text-teal-400"></i>
                  <a href="mailto:help@poljanadoo.com" className="hover:text-teal-400 transition-colors">help@poljanadoo.com</a>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-phone-line text-teal-400"></i>
                  <a href="tel:+17167109697" className="hover:text-teal-400 transition-colors">+1 7167109697</a>
                </div>
              </div>
            </div>

            {/* Message Form */}
            <div>
              <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-4">Send Message</h4>
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="email"
                  name="email"
                  placeholder="Your email"
                  required
                  className="w-full px-4 py-2 bg-emerald-800 border border-emerald-700 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors"
                />
                <textarea
                  name="message"
                  placeholder="Your message"
                  required
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-2 bg-emerald-800 border border-emerald-700 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-none focus:border-teal-400 transition-colors resize-none"
                ></textarea>
                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  Send
                </button>
              </form>
            </div>
          </div>

          {/* Large Brand Typography */}
          <div className="text-center">
            <div className="text-9xl font-black text-orange-100/20 tracking-tighter" style={{ fontFamily: 'Inter, sans-serif' }}>
              POLJANA
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-950 text-gray-400 py-6 px-16">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div>© 2025 Poljana d. o. o. All rights reserved.</div>
          <div>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
